import { Routes, Route, Navigate } from 'react-router-dom'
import { SiteHeader } from '@/components/SiteHeader'
import { Footer } from '@/components/Footer'
import { ContentShell } from '@/components/ContentShell'

// Base Web & Styletron
import { Client as Styletron } from 'styletron-engine-atomic';
import { Provider as StyletronProvider } from 'styletron-react';
import { LightTheme, BaseProvider } from 'baseui';

// State sync
import { useSync } from '@/lib/hooks/use-sync';

// Pages
import OhmsLawQuestionsPage from '@/pages/OhmsLawQuestions'
import ResistorQuestionsPage from '@/pages/ResistorQuestions'
import CurrentQuestionsPage from '@/pages/CurrentQuestions'
import PowerQuestionsPage from '@/pages/PowerQuestions'
import VoltageDividerQuestionsPage from '@/pages/VoltageDividerQuestions'
import TheveninQuestionsPage from '@/pages/TheveninQuestions'
import CapacitorQuestionsPage from '@/pages/CapacitorQuestions'
import InductorQuestionsPage from '@/pages/InductorQuestions'
import NetworkQuestionsPage from '@/pages/NetworkQuestions'
import AboutPage from '@/pages/About'
import PrivacyPage from '@/pages/Privacy'
import TermsPage from '@/pages/Terms'
import SmithChartPage from '@/pages/SmithChart'
import NotFoundPage from '@/pages/NotFound'

const engine = new Styletron();

const navItems = [
  {
    name: "DC Fundamentals",
    href: "/questions/ohmslaw",
    items: [
      {
        name: "Ohm's Law",
        href: "/questions/ohmslaw",
        description: "V = IR: Relationship between voltage, current, and resistance",
      },
      {
        name: "Power",
        href: "/questions/power",
        description: "P = IV, P = I²R, P = V²/R power calculations",
      },
      {
        name: "Resistors",
        href: "/questions/resistors",
        description: "Series and parallel resistor combinations",
      },
      {
        name: "Voltage Divider",
        href: "/questions/voltage-divider",
        description: "Voltage division in series resistor circuits",
      },
      {
        name: "Current",
        href: "/questions/current",
        description: "Kirchhoff's current law and current division",
      },
    ],
  },
  {
    name: "Network Theorems",
    href: "/questions/thevenin",
    items: [
      {
        name: "Thevenin & Norton",
        href: "/questions/thevenin",
        description: "Equivalent circuit simplification techniques",
      },
    ],
  },
  {
    name: "Energy Storage",
    href: "/questions/capacitor",
    items: [
      {
        name: "Capacitors",
        href: "/questions/capacitor",
        description: "RC circuits, time constants, and capacitor combinations",
      },
      {
        name: "Inductors",
        href: "/questions/inductor",
        description: "RL circuits, time constants, and inductor combinations",
      },
    ],
  },
  {
    name: "Networks",
    href: "/questions/network",
    items: [
      {
        name: "Subnetting",
        href: "/questions/network",
        description: "IP addressing and subnet calculations",
      },
    ],
  },
  {
    name: "Tools",
    href: "/extra",
    items: [
      {
        name: "Smith Chart",
        href: "/extra/smith-chart",
        description: "Visualize complex impedance",
      },
    ],
  },
]

function App() {
  // Initialize state sync (session + WebSocket)
  useSync();

  return (
    <StyletronProvider value={engine}>
      <BaseProvider theme={LightTheme}>
        <div className="container mx-auto px-4 pb-12 pt-4 max-w-screen-2xl">
          <div className="navigation w-full">
            <SiteHeader navItems={navItems} />
          </div>

          <ContentShell>
            <Routes>
              <Route path="/" element={<Navigate to="/questions/ohmslaw" replace />} />
              {/* DC Fundamentals */}
              <Route path="/questions/ohmslaw" element={<OhmsLawQuestionsPage />} />
              <Route path="/questions/power" element={<PowerQuestionsPage />} />
              <Route path="/questions/resistors" element={<ResistorQuestionsPage />} />
              <Route path="/questions/voltage-divider" element={<VoltageDividerQuestionsPage />} />
              <Route path="/questions/current" element={<CurrentQuestionsPage />} />
              {/* Network Theorems */}
              <Route path="/questions/thevenin" element={<TheveninQuestionsPage />} />
              {/* Energy Storage */}
              <Route path="/questions/capacitor" element={<CapacitorQuestionsPage />} />
              <Route path="/questions/inductor" element={<InductorQuestionsPage />} />
              {/* Networking */}
              <Route path="/questions/network" element={<NetworkQuestionsPage />} />
              {/* Static pages */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/extra/smith-chart" element={<SmithChartPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </ContentShell>

          <Footer />
        </div>
      </BaseProvider>
    </StyletronProvider>
  )
}

export default App
